// WebSocket protocol client. Handles connect/reconnect, message routing.

export class ProtocolClient {
  constructor({ apiBase, siteKey, mode, onSession, onAck, onReject, onNewTemplate, onRetry, onPass, onFail, onError }) {
    this._apiBase = apiBase.replace(/^http/, "ws");
    this._siteKey = siteKey;
    this._mode = mode || "captcha";
    this._ws = null;
    this._closed = false;
    this._reconnectAttempts = 0;
    this._handlers = { onSession, onAck, onReject, onNewTemplate, onRetry, onPass, onFail, onError };
    this._heartbeatInterval = null;
  }

  connect() {
    const url = `${this._apiBase}/captcha/v1/ws`;
    this._ws = new WebSocket(url);
    this._ws.addEventListener("open", () => {
      this._reconnectAttempts = 0;
      const hello = { type: "hello", site_key: this._siteKey };
      if (this._mode !== "captcha") hello.mode = this._mode;
      this._ws.send(JSON.stringify(hello));
      this._heartbeatInterval = setInterval(() => {
        if (this._ws?.readyState === WebSocket.OPEN) {
          this._ws.send(JSON.stringify({ type: "ping" }));
        }
      }, 30_000);
    });
    this._ws.addEventListener("message", (evt) => {
      let msg;
      try { msg = JSON.parse(evt.data); } catch { return; }
      this._dispatch(msg);
    });
    this._ws.addEventListener("close", () => {
      this._teardownHeartbeat();
      if (!this._closed && this._reconnectAttempts < 3) {
        const delay = Math.min(5000, 500 * 2 ** this._reconnectAttempts);
        this._reconnectAttempts++;
        setTimeout(() => this.connect(), delay);
      } else if (!this._closed) {
        this._handlers.onFail?.({ reason: "connection_lost" });
      }
    });
    this._ws.addEventListener("error", (err) => {
      this._handlers.onError?.(err);
    });
  }

  _dispatch(msg) {
    switch (msg.type) {
      case "session":       this._handlers.onSession?.(msg); break;
      case "ack":           this._handlers.onAck?.(msg); break;
      case "reject":        this._handlers.onReject?.(msg); break;
      case "new_template":  this._handlers.onNewTemplate?.(msg); break;
      case "retry":         this._handlers.onRetry?.(msg); break;
      case "pass":          this._handlers.onPass?.(msg); break;
      case "fail":          this._handlers.onFail?.(msg); break;
      case "pong":          break;
    }
  }

  sendImprovement(nonce, hashHex, templateId) {
    const msg = { type: "improvement", nonce, hash: hashHex };
    if (templateId) msg.template_id = templateId;
    this._send(msg);
  }

  sendCheckboxClick(ts) {
    this._send({ type: "checkbox_clicked", ts });
  }

  sendFinalize() {
    this._send({ type: "finalize" });
  }

  close() {
    this._closed = true;
    this._teardownHeartbeat();
    if (this._ws) this._ws.close();
  }

  _send(obj) {
    if (this._ws?.readyState === WebSocket.OPEN) {
      this._ws.send(JSON.stringify(obj));
    }
  }

  _teardownHeartbeat() {
    if (this._heartbeatInterval) {
      clearInterval(this._heartbeatInterval);
      this._heartbeatInterval = null;
    }
  }
}
